import { DownloadOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Skeleton,
  Statistic,
  Table,
  Typography,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { downloadFile, errorMessage } from '../../api/client';
import { fetchProfitPaymentImageUrl, useProfit, useProfitPayments, useRecordPayment } from '../../api/hooks';
import { useAuth } from '../../context/AuthContext';
import type { ProfitPayment } from '../../types';
import { apiDate, date, money, percent } from '../../utils/format';
import { paymentMethodOptions } from '../../utils/paymentMethods';
import PendingDocumentPicker from './PendingDocumentPicker';
import TransactionImageLink from './TransactionImageLink';


export default function ProfitTab({ investorId }: { investorId: number }) {
  const [range, setRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([dayjs().startOf('month'), dayjs()]);
  const { canWrite, hasRole } = useAuth();
  // Investors get a single statement PDF from the Ledger tab instead of per-row receipts.
  const showReceipts = !hasRole('Investor');
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const [transactionImage, setTransactionImage] = useState<File | null>(null);

  const from = apiDate(range[0]);
  const to = apiDate(range[1]);
  const { data: profit, isLoading } = useProfit(investorId, from, to);
  const { data: payments } = useProfitPayments(investorId);
  const recordPayment = useRecordPayment(investorId);

  const onFinish = async (values: Record<string, unknown>) => {
    try {
      await recordPayment.mutateAsync({
        amount: values.amount as number,
        paymentDate: apiDate(values.paymentDate as dayjs.Dayjs)!,
        paymentMethod: values.paymentMethod as string | undefined,
        receiptNo: values.receiptNo as string | undefined,
        notes: values.notes as string | undefined,
        transactionImage,
      });
      message.success('Profit payment recorded.');
      form.resetFields();
      setTransactionImage(null);
      setOpen(false);
    } catch (err) {
      message.error(errorMessage(err));
    }
  };

  // Flattened accrual segments — this is the audit trail behind the accrued total.
  const segments = (profit?.tranches ?? []).flatMap((tranche) =>
    tranche.segments.map((segment, index) => ({
      ...segment,
      key: `${tranche.investmentId}-${index}`,
      investmentId: tranche.investmentId,
    })),
  );

  return (
    <>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={10}>
            <DatePicker.RangePicker
              style={{ width: '100%' }}
              value={range}
              allowClear={false}
              onChange={(values) => values && setRange(values as [dayjs.Dayjs, dayjs.Dayjs])}
            />
          </Col>
          {canWrite && (
            <Col>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)}>
                Record payment
              </Button>
            </Col>
          )}
        </Row>
      </Card>

      {isLoading || !profit ? (
        <Skeleton active />
      ) : (
        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <Card>
              <Statistic title="Accrued profit (period)" value={money(profit.accruedProfit)} prefix="PKR" />
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card>
              <Statistic title="Paid in period" value={money(profit.paidProfit)} prefix="PKR" />
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card>
              <Statistic
                title="Outstanding"
                value={money(profit.outstandingProfit)}
                prefix="PKR"
                valueStyle={{ color: profit.outstandingProfit > 0 ? '#cf1322' : '#3f8600' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      <Card title="Accrual breakdown" style={{ marginTop: 16 }} size="small">
        <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
          Daily profit = (remaining principal × monthly rate ÷ 100) ÷ 30, accrued per day.
        </Typography.Paragraph>
        <Table
          rowKey="key"
          size="small"
          dataSource={segments}
          pagination={false}
          scroll={{ x: 'max-content' }}
          columns={[
            { title: 'Tranche', dataIndex: 'investmentId', render: (v: number) => `#${v}` },
            { title: 'From', dataIndex: 'from', render: date },
            { title: 'To', dataIndex: 'to', render: date },
            { title: 'Days', dataIndex: 'days', align: 'right' },
            { title: 'Principal', dataIndex: 'principal', align: 'right', render: money },
            { title: 'Rate', dataIndex: 'profitRate', align: 'right', render: percent },
            {
              title: 'Profit',
              dataIndex: 'profit',
              align: 'right',
              render: (v: number) => <strong>{money(v)}</strong>,
            },
          ]}
        />
      </Card>

      <Card title="Payment history" style={{ marginTop: 16 }} size="small">
        <Table<ProfitPayment>
          rowKey="id"
          size="small"
          dataSource={payments ?? []}
          scroll={{ x: 'max-content' }}
          columns={[
            { title: 'Date', dataIndex: 'paymentDate', render: date },
            { title: 'Amount', dataIndex: 'amount', align: 'right', render: money },
            { title: 'Method', dataIndex: 'paymentMethod', render: (v: string | null) => v ?? '—' },
            { title: 'Receipt no', dataIndex: 'receiptNo', render: (v: string | null) => v ?? '—' },
            { title: 'Notes', dataIndex: 'notes', render: (v: string | null) => v ?? '—' },
            {
              title: 'Transaction proof',
              render: (_, row) => (
                <TransactionImageLink
                  exists={row.hasTransactionImage}
                  title={`Transaction proof — payment #${row.id}`}
                  fetchUrl={() => fetchProfitPaymentImageUrl(investorId, row.id)}
                />
              ),
            },
            ...(showReceipts
              ? [
                  {
                    title: 'Receipt',
                    render: (_: unknown, row: ProfitPayment) => (
                      <Button
                        type="link"
                        size="small"
                        icon={<DownloadOutlined />}
                        onClick={() =>
                          downloadFile(
                            `/receipts/profit-payment/${row.id}`,
                            `profit_payment_receipt_${row.id}.pdf`,
                          ).catch((err) => message.error(errorMessage(err)))
                        }
                      >
                        PDF
                      </Button>
                    ),
                  },
                ]
              : []),
          ]}
        />
      </Card>

      <Modal
        title="Record profit payment"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={recordPayment.isPending}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onFinish} preserve={false} initialValues={{ paymentDate: dayjs() }}>
          <Form.Item
            name="amount"
            label="Amount (PKR)"
            rules={[{ required: true }, { type: 'number', min: 0.01, message: 'Amount must be greater than zero.' }]}
          >
            <InputNumber min={0.01} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="paymentDate" label="Payment date" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} disabledDate={(d) => d.isAfter(dayjs(), 'day')} />
          </Form.Item>
          <Form.Item name="paymentMethod" label="Payment method">
            <Select options={paymentMethodOptions} allowClear />
          </Form.Item>
          <Form.Item name="receiptNo" label="Receipt no (auto-generated if blank)">
            <Input />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>

          <PendingDocumentPicker
            type="profit-payment-image"
            label="Transaction proof (optional)"
            hint="Receipt or transfer screenshot proving the investor received the payment · JPG, PNG or WebP · max 5 MB"
            file={transactionImage}
            onChange={setTransactionImage}
          />
        </Form>
      </Modal>
    </>
  );
}
